/**
 * GS1 / DataMatrix — encoding, parsing y validaciones.
 *
 * ANMAT adoptó el estándar GS1 para trazabilidad farmacéutica. Cada unidad
 * lleva un DataMatrix que codifica los Application Identifiers (AI):
 *   (01) GTIN-14       — identificador global del producto (14 dígitos, check digit mod-10)
 *   (17) vencimiento   — YYMMDD; convención día 00 cuando solo se conoce MM/YYYY
 *   (10) lote          — variable length (máx 20), termina con FNC1
 *   (21) n° de serie   — variable length (máx 20), último AI
 *
 * Orden recomendado por GS1 GenSpec: AIs fijos primero (01, 17), variables al
 * final (10, 21). bwip-js con `parsefnc: true` interpreta los paréntesis como
 * FNC1 cuando corresponde.
 *
 * El parser (`parsearStringGs1`) hace el camino inverso sobre lo que entrega un
 * escáner en modo teclado. Ver la sección "Parser" más abajo para los formatos
 * que acepta y qué pasa cuando falta el separador entre AIs variables.
 */

export interface Gs1Input {
  gtin: string;
  lote: string;
  /** Formato `MM/YYYY`. */
  vencimiento: string;
  serieId: string;
}

/**
 * Construye el string GS1 listo para encodear en DataMatrix.
 * NO valida el contenido — los callers deben validar GTIN y formato de vencimiento
 * con `validarGtin14` y `validarVencimientoMmYyyy` antes.
 */
export function construirStringGs1({ gtin, lote, vencimiento, serieId }: Gs1Input): string {
  const venc = vencimientoAFormatoGs1(vencimiento);
  return `(01)${gtin}(17)${venc}(10)${lote}(21)${serieId}`;
}

/**
 * Convierte `MM/YYYY` → `YYMMDD` (día 00, convención GS1 para vencimientos sin día).
 * Retorna `'000000'` si el formato es inválido — los callers deberían validar antes
 * con `validarVencimientoMmYyyy`.
 */
export function vencimientoAFormatoGs1(venc: string): string {
  const m = /^(\d{2})\/(\d{4})$/.exec(venc ?? '');
  // Si match falla, m es null. Si pasa, los grupos están garantizados pero
  // `noUncheckedIndexedAccess` los tipa como `string | undefined` — guardas explícitas.
  const mm = m?.[1];
  const yyyy = m?.[2];
  if (!mm || !yyyy) return '000000';
  return `${yyyy.slice(2)}${mm}00`;
}

/**
 * Verifica que un string sea `MM/YYYY` con mes válido (01-12).
 * NO valida que la fecha sea futura — eso es decisión del caller.
 */
export function validarVencimientoMmYyyy(venc: string): boolean {
  const m = /^(\d{2})\/(\d{4})$/.exec(venc ?? '');
  const mmStr = m?.[1];
  if (!mmStr) return false;
  const mes = parseInt(mmStr, 10);
  return mes >= 1 && mes <= 12;
}

/**
 * Valida GTIN-14 con check digit modulo-10 (algoritmo GS1).
 *
 * Algoritmo: tomar los 13 primeros dígitos, sumar (impares × 3 + pares × 1)
 * desde la derecha, el check digit es (10 - sum mod 10) mod 10.
 *
 * Ejemplos válidos del seed Everex:
 *   07791234560011 (BIRAT)
 *   07791234560028 (MIZARID)
 */
export function validarGtin14(gtin: string): boolean {
  if (typeof gtin !== 'string' || !/^\d{14}$/.test(gtin)) return false;
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const d = Number(gtin[i]);
    // Posiciones (1-indexed) impares × 3, pares × 1 — equivale a: posición par i (0-indexed) × 3
    sum += d * (i % 2 === 0 ? 3 : 1);
  }
  const check = (10 - (sum % 10)) % 10;
  return check === Number(gtin[13]);
}

/**
 * Calcula el check digit para un GTIN-14 dado solo los primeros 13 dígitos.
 * Útil para generar GTINs de prueba que cumplen el estándar.
 */
export function calcularCheckDigitGtin14(prefix13: string): number {
  if (!/^\d{13}$/.test(prefix13)) {
    throw new Error('calcularCheckDigitGtin14: requiere exactamente 13 dígitos');
  }
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const d = Number(prefix13[i]);
    sum += d * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

// =============================================================================
// PARSER — lectura del escáner → AIs
// =============================================================================
//
// Formatos aceptados:
//   1. Crudo con separador: `01<gtin>17<yymmdd>10<lote><GS>21<serie>`. Es lo que
//      transmite un lector en modo teclado si el GS (ASCII 29, FNC1) llega al
//      campo; el front lo inserta al detectar Ctrl+] (VerificacionEscaneo).
//   2. Legible con paréntesis: `(01)…(17)…(10)…(21)…`.
//   3. Crudo SIN separador: `01<gtin>17<yymmdd>10<lote>21<serie>` todo pegado.
//      Es lo que se vio con un Zebra DS2208 en macOS con layout en español (el
//      Ctrl+] se pierde). Acá el parser NO puede saber dónde termina el lote y
//      empieza la serie — `21` es un texto válido dentro de un lote. Devuelve el
//      tramo crudo en `colaSinSeparador` para que el caller lo resuelva con
//      información externa (ej. las series esperadas de un remito).
//
// Se acepta un prefijo AIM opcional (`]d2` DataMatrix GS1, `]C1` GS1-128,
// `]e0` DataBar) y se tolera CR/LF final.

/** Carácter FNC1 / GS (ASCII 29): separador entre AIs de longitud variable. */
export const GS1_SEPARADOR = '\x1d';

/**
 * Representación visible del GS (U+241D, "SYMBOL FOR GROUP SEPARATOR"). El
 * campo de lectura del front inserta este carácter en lugar del ASCII 29 para
 * que el operador lo vea y para no depender de cómo trate el navegador un
 * carácter de control dentro de un input. El parser acepta ambos.
 */
export const GS1_SEPARADOR_VISIBLE = '␝';

/** Unifica las dos representaciones del separador en el ASCII 29. */
export function normalizarSeparadoresGs1(s: string): string {
  return (s ?? '').split(GS1_SEPARADOR_VISIBLE).join(GS1_SEPARADOR);
}

/** ¿El texto contiene el separador en alguna de sus dos formas? */
export function tieneSeparadorGs1(s: string): boolean {
  return (s ?? '').includes(GS1_SEPARADOR) || (s ?? '').includes(GS1_SEPARADOR_VISIBLE);
}

/** AIs de longitud fija: código → largo del dato. */
const AI_FIJOS: Readonly<Record<string, number>> = {
  '00': 18,
  '01': 14,
  '02': 14,
  '11': 6,
  '12': 6,
  '13': 6,
  '15': 6,
  '16': 6,
  '17': 6,
  '20': 2,
  '410': 13,
  '411': 13,
  '412': 13,
  '413': 13,
  '414': 13,
  '415': 13,
  '416': 13,
  '417': 13,
};

/** AIs de longitud variable: código → largo máximo del dato. */
const AI_VARIABLES: Readonly<Record<string, number>> = {
  '10': 20,
  '21': 20,
  '22': 20,
  '30': 8,
  '37': 8,
  '240': 30,
  '241': 30,
  '250': 30,
  '251': 30,
  '254': 20,
  '90': 30,
  '91': 90,
  '92': 90,
  '93': 90,
  '94': 90,
  '95': 90,
  '96': 90,
  '97': 90,
  '98': 90,
  '99': 90,
};

function largoFijo(ai: string): number | null {
  const fijo = AI_FIJOS[ai];
  if (fijo != null) return fijo;
  // 310x–316x, 320x–369x: medidas con indicador decimal; AI de 4 dígitos, dato de 6.
  if (/^3[1-6]\d\d$/.test(ai)) return 6;
  return null;
}

export interface Gs1Parseado {
  /** true si se reconoció al menos un AI conocido. */
  esGs1: boolean;
  /** Identificador de simbología AIM sin el corchete (ej. `d2`), si vino. */
  simbologia: string | null;
  /** Todos los AIs interpretados, por código. */
  ais: Readonly<Record<string, string>>;
  /** AI 01, tal como vino (14 dígitos). */
  gtin: string | null;
  /** AI 17, `YYMMDD` tal como vino. */
  vencimiento: string | null;
  /** AI 10. Puede ser tentativo si `colaSinSeparador` no es null. */
  lote: string | null;
  /** AI 21. Puede ser tentativo si `colaSinSeparador` no es null. */
  serie: string | null;
  /**
   * Tramo crudo, desde el AI inclusive, del último AI de longitud variable que
   * terminó por fin de string sin separador (ej. `10PA1710002921041793`). Si no
   * es null, `lote` y/o `serie` pueden contener AIs concatenados y el caller
   * debe desambiguar con información externa.
   */
  colaSinSeparador: string | null;
  /** Datos que no se pudieron interpretar (AI desconocido o dato truncado). */
  resto: string | null;
}

/** Heurística barata: ¿esta lectura parece un string GS1 y no una serie suelta? */
export function pareceLecturaGs1(raw: string): boolean {
  const s = (raw ?? '').trim();
  if (!s) return false;
  if (s.startsWith(']')) return true;
  if (tieneSeparadorGs1(s)) return true;
  if (/^\(\d{2,4}\)/.test(s)) return true;
  return /^01\d{14}/.test(s);
}

export function parsearStringGs1(raw: string): Gs1Parseado {
  let s = normalizarSeparadoresGs1((raw ?? '').replace(/[\r\n]+$/g, '').trim());
  const ais: Record<string, string> = {};
  let simbologia: string | null = null;
  let resto: string | null = null;
  let colaSinSeparador: string | null = null;

  const aim = /^\]([A-Za-z])(\d)/.exec(s);
  if (aim) {
    simbologia = `${aim[1] ?? ''}${aim[2] ?? ''}`;
    s = s.slice(3);
  }

  if (/^\(\d{2,4}\)/.test(s)) {
    // Formato legible: (AI)valor(AI)valor…  Los valores no pueden contener "(".
    const re = /\((\d{2,4})\)([^(]*)/g;
    let m: RegExpExecArray | null;
    let consumido = 0;
    while ((m = re.exec(s)) !== null) {
      const ai = m[1] ?? '';
      ais[ai] = (m[2] ?? '').replace(new RegExp(GS1_SEPARADOR, 'g'), '');
      consumido = m.index + m[0].length;
    }
    if (consumido < s.length) resto = s.slice(consumido);
  } else {
    let i = 0;
    while (i < s.length) {
      if (s[i] === GS1_SEPARADOR) {
        i += 1;
        continue;
      }
      let ai: string | null = null;
      let largo: number | null = null;
      let variable = false;
      for (const n of [2, 3, 4]) {
        const cand = s.slice(i, i + n);
        if (cand.length < n) break;
        const f = largoFijo(cand);
        if (f != null) {
          ai = cand;
          largo = f;
          break;
        }
        if (cand in AI_VARIABLES) {
          ai = cand;
          variable = true;
          break;
        }
      }
      if (!ai) {
        resto = s.slice(i);
        break;
      }
      const inicioAi = i;
      i += ai.length;
      if (!variable && largo != null) {
        const val = s.slice(i, i + largo);
        if (val.length < largo) {
          resto = s.slice(inicioAi);
          break;
        }
        ais[ai] = val;
        i += largo;
      } else {
        const fin = s.indexOf(GS1_SEPARADOR, i);
        if (fin === -1) {
          ais[ai] = s.slice(i);
          colaSinSeparador = s.slice(inicioAi);
          i = s.length;
        } else {
          ais[ai] = s.slice(i, fin);
          i = fin + 1;
        }
      }
    }
  }

  return {
    esGs1: Object.keys(ais).length > 0,
    simbologia,
    ais,
    gtin: ais['01'] ?? null,
    vencimiento: ais['17'] ?? null,
    lote: ais['10'] ?? null,
    serie: ais['21'] ?? null,
    colaSinSeparador,
    resto,
  };
}

/**
 * Normaliza un GTIN a 14 dígitos (GTIN-8/12/13 → relleno con ceros a la
 * izquierda). Null si no es numérico o el largo no es GS1. Sirve para comparar
 * el AI 01 del escáner (siempre 14) contra el `codigoDeBarras` del ERP (puede
 * venir de 13).
 */
export function gtinNormalizado(gtin: string | null | undefined): string | null {
  const s = (gtin ?? '').trim();
  if (!/^\d{8}$|^\d{12,14}$/.test(s)) return null;
  return s.padStart(14, '0');
}

/**
 * `YYMMDD` del AI 17 → `MM/YYYY` para mostrar. Null si no tiene el formato.
 * Ignora el día a propósito: los vencimientos farmacéuticos se expresan por
 * mes y el ERP guarda la fecha completa con día arbitrario.
 */
export function vencimientoGs1AMesAnio(yymmdd: string | null | undefined): string | null {
  const m = /^(\d{2})(\d{2})\d{2}$/.exec(yymmdd ?? '');
  const yy = m?.[1];
  const mm = m?.[2];
  if (!yy || !mm) return null;
  return `${mm}/20${yy}`;
}
